'use client'

import React from 'react'
import { motion } from 'framer-motion'

type ProgressiveBlurProps = {
  className?: string
  direction?: 'left' | 'right' | 'top' | 'bottom'
  blurIntensity?: number
  children?: React.ReactNode
}

export function ProgressiveBlur({
  className = '',
  direction = 'right',
  blurIntensity = 5,
  children,
}: ProgressiveBlurProps) {
  let gradientDirection: string
  
  switch (direction) {
    case 'left':
      gradientDirection = 'to left'
      break
    case 'right':
      gradientDirection = 'to right'
      break
    case 'top':
      gradientDirection = 'to top'
      break
    case 'bottom':
      gradientDirection = 'to bottom'
      break
    default:
      gradientDirection = 'to right'
  }
  
  const maxBlur = blurIntensity * 10 // Max blur in pixels
  
  const steps = 10
  let gradientStops = []
  
  // Create gradient stops with increasing blur amounts
  for (let i = 0; i <= steps; i++) {
    const percent = (i / steps) * 100
    const blurAmount = (i / steps) * maxBlur
    gradientStops.push({
      percent,
      blurAmount: Math.round(blurAmount * 10) / 10, // Round to 1 decimal place
    })
  }
  
  return (
    <div className={`relative z-10 ${className}`}>
      <div
        className="h-full w-full"
        style={{
          background: `linear-gradient(${gradientDirection}, transparent 0%, rgba(var(--background-rgb), 0.25) 25%, rgba(var(--background-rgb), 0.5) 50%, rgba(var(--background-rgb), 0.75) 75%, rgba(var(--background-rgb), 1) 100%)`,
        }}
      >
        {children}
      </div>
      
      {gradientStops.map((stop, index) => (
        <div
          key={index}
          className="absolute inset-0"
          style={{
            backdropFilter: `blur(${stop.blurAmount}px)`,
            WebkitBackdropFilter: `blur(${stop.blurAmount}px)`,
            clipPath: direction === 'left'
              ? `polygon(0% 0%, ${stop.percent}% 0%, ${stop.percent}% 100%, 0% 100%)`
              : direction === 'right'
              ? `polygon(${100 - stop.percent}% 0%, 100% 0%, 100% 100%, ${100 - stop.percent}% 100%)`
              : direction === 'top'
              ? `polygon(0% 0%, 100% 0%, 100% ${stop.percent}%, 0% ${stop.percent}%)`
              : `polygon(0% ${100 - stop.percent}%, 100% ${100 - stop.percent}%, 100% 100%, 0% 100%)`,
            pointerEvents: 'none',
          }}
        />
      ))}
    </div>
  )
} 